import * as WEBIFC from 'web-ifc'

// Parallel web-ifc parser kept alongside the Fragments model. Reason:
// the IFC → Fragments conversion drops the original IFC attributes
// that describe geometry: IfcLocalPlacement (PlacementRelTo chain,
// RelativePlacement axes) and IfcShapeRepresentation (SweptSolid /
// BREP / Tessellation / MappedRepresentation items). To inspect those
// at the schema level we need the original STEP entities. We keep an
// IfcAPI instance per loaded model and answer queries by expressID
// (which equals the localId Fragments uses).

export type RawIfcStatus = 'idle' | 'loading' | 'ready' | 'failed'

interface ModelEntry {
  api: WEBIFC.IfcAPI
  modelID: number
  // Memoize type-code → name lookups; web-ifc has thousands of codes
  // and we hit the same handful for any given model.
  typeNames: Map<number, string>
  // Lazy map: RelatingGroup expressID → RelatedObjects expressIDs.
  // Built on first request by scanning all IfcRelAssignsToGroup; the
  // alternative (GetLine with inverse=true reading IsGroupedBy) is
  // unreliable across web-ifc versions and silently returns empty.
  groupMembers?: Map<number, number[]>
  // Lazy map: GlobalId → expressID for the non-geometric aggregation
  // types (IfcZone, IfcSystem, IfcGroup, ...) that Fragments leaves out
  // of its own guid index. Lets BCF viewpoints that reference these by
  // GlobalId still resolve. Built over a fixed type list on first call.
  aggregationGuidIndex?: Map<string, number>
}

/** One IfcRelSpaceBoundary relation, indexed from both sides.
 *  `elementExpressID` is null for virtual boundaries (where the IFC
 *  has PhysicalOrVirtualBoundary = VIRTUAL and RelatedBuildingElement
 *  is omitted). */
export interface RawSpaceBoundary {
  relExpressID: number
  /** Subtype name (IfcRelSpaceBoundary / IfcRelSpaceBoundary1stLevel
   *  / IfcRelSpaceBoundary2ndLevel), useful so the user sees which
   *  IFC schema level the boundary was authored at. */
  relCategory: string
  spaceExpressID: number
  elementExpressID: number | null
  /** Optional Name on the relation itself (rare). */
  name: string | null
  /** Enum: PHYSICAL | VIRTUAL | NOTDEFINED. */
  physicalOrVirtual: string | null
  /** Enum: INTERNAL | EXTERNAL | EXTERNAL_EARTH | EXTERNAL_WATER
   *  | EXTERNAL_FIRE | NOTDEFINED. */
  internalOrExternal: string | null
  /** Express ID of the ConnectionGeometry line (or null if the
   *  boundary has no geometry, common in older exports / virtual
   *  boundaries). Exposed so callers can drill into the raw surface. */
  connectionGeometryExpressID: number | null
  /** Surface area in the model's length-unit² (typically m²) when
   *  computable from the ConnectionGeometry. Null when the surface
   *  shape is unsupported (we handle the two common shapes:
   *  IfcConnectionSurfaceGeometry → IfcFaceSurface with PolyLoop,
   *  and IfcConnectionSurfaceGeometry → IfcCurveBoundedPlane with
   *  Polyline). Composite curves / parametric surfaces / arcs are
   *  returned as null rather than approximated. */
  area: number | null
}

interface ModelState {
  status: RawIfcStatus
  error?: string
  entry?: ModelEntry
  // Cached bytes so we can reload after a manual disable + re-enable
  // without re-reading the original file.
  bytes?: Uint8Array
}

class RawIfcStore {
  private models = new Map<string, ModelState>()
  private listeners = new Set<() => void>()

  // Diretório do WASM do web-ifc. Em produção (browser/Vite) é servido
  // em '/wasm/'; os testes de conformidade que correm em Node precisam
  // de o apontar para node_modules/web-ifc/ (build node do web-ifc).
  // Hook exclusivo de testes: NÃO altera o comportamento de produção,
  // que continua a usar os valores por omissão abaixo.
  private wasmPath = '/wasm/'
  private wasmPathAbsolute = true

  /** Só para testes: redireciona o diretório do WASM do web-ifc. Em
   *  produção o valor por omissão ('/wasm/', absoluto) mantém-se. */
  setWasmPath(path: string, absolute = true): void {
    this.wasmPath = path
    this.wasmPathAbsolute = absolute
  }

  // Stash bytes for a model so we can defer/redo the parse without
  // re-reading the file. Called by useViewer regardless of whether
  // the parallel parser is enabled.
  cacheBytes(modelId: string, bytes: Uint8Array): void {
    const state = this.models.get(modelId)
    if (state) {
      state.bytes = bytes
    } else {
      this.models.set(modelId, { status: 'idle', bytes })
    }
    this.notify()
  }

  // Read-only accessor used by the IFCEdit / BoQ bridge so the
  // server-side session can be primed without forcing the user to
  // re-pick the file. The bytes are kept alive as long as the model
  // is loaded; disposing the model evicts them.
  getCachedBytes(modelId: string): Uint8Array | undefined {
    return this.models.get(modelId)?.bytes
  }

  async loadModel(modelId: string, bytes?: Uint8Array): Promise<void> {
    let state = this.models.get(modelId)
    if (state?.entry) {
      // Already loaded: no-op. The UI will show the existing 'ready'
      // status; if the caller wants a hard reload it should call
      // unloadModel first.
      return
    }
    const useBytes = bytes ?? state?.bytes
    if (!useBytes) {
      // Ponto de estrangulamento de tudo o que lê o IFC: o IDS, o BoQ,
      // a Tabela, o IFCEdit, a escrita de georreferenciação e o
      // assistente passam todos por aqui. Um modelo aberto em Fragments
      // (.frag) não traz o IFC, e o que chegava ao utilizador era
      // "rawIfc.loadModel: no bytes available for model", em inglês e
      // sem dizer o que fazer. A mensagem é dita uma vez, aqui, e todos
      // a herdam.
      throw new Error(
        'Este modelo não tem o IFC por trás: foi aberto a partir de um ' +
          'ficheiro Fragments (.frag), que só traz geometria já ' +
          'derivada. Esta operação lê o IFC. Abre o .ifc original para ' +
          'a poder usar.',
      )
    }
    state = state ?? { status: 'idle' }
    state.status = 'loading'
    state.error = undefined
    state.bytes = useBytes
    this.models.set(modelId, state)
    this.notify()
    try {
      const api = new WEBIFC.IfcAPI()
      api.SetWasmPath(this.wasmPath, this.wasmPathAbsolute)
      // Prototype patched in useViewer to force single-thread.
      await api.Init()
      const modelID = api.OpenModel(useBytes)
      state.entry = { api, modelID, typeNames: new Map() }
      state.status = 'ready'
      this.notify()
    } catch (err) {
      state.status = 'failed'
      state.error = err instanceof Error ? err.message : String(err)
      state.entry = undefined
      this.notify()
      throw err
    }
  }

  unloadModel(modelId: string, keepBytes = false): void {
    const state = this.models.get(modelId)
    if (!state) return
    if (state.entry) {
      try {
        state.entry.api.CloseModel(state.entry.modelID)
      } catch {
        /* ignore */
      }
      state.entry = undefined
    }
    if (keepBytes) {
      state.status = 'idle'
      state.error = undefined
      this.models.set(modelId, state)
    } else {
      this.models.delete(modelId)
    }
    this.notify()
  }

  getStatus(modelId: string): RawIfcStatus {
    return this.models.get(modelId)?.status ?? 'idle'
  }

  getError(modelId: string): string | undefined {
    return this.models.get(modelId)?.error
  }

  hasModel(modelId: string): boolean {
    return this.models.get(modelId)?.entry !== undefined
  }

  hasCachedBytes(modelId: string): boolean {
    return !!this.models.get(modelId)?.bytes
  }

  /** Escape hatch for modules that need direct access to the underlying
   * web-ifc handles: currently only the BoQ module's client-side
   * calculator, which calls StreamMeshes / GetGeometry / GetAllLines
   * directly to avoid sending IFC bytes to the backend. Returns null
   * when the model isn't loaded. Callers must NOT close the model. */
  getRawHandles(
    modelId: string,
  ): { api: WEBIFC.IfcAPI; modelID: number } | null {
    const entry = this.models.get(modelId)?.entry
    return entry ? { api: entry.api, modelID: entry.modelID } : null
  }

  /** Counts of non-geometric IFC entity types that Fragments tends to
   * exclude from getItemsOfCategories (logical groupings, systems,
   * grids). The user's "Entidades IFC" panel uses this to surface
   * IfcSystem / IfcGroup / IfcAssembly / IfcGrid even when they have
   * no 3D geometry. Each returned entry includes the express IDs so
   * the UI can offer "select all members" actions later. */
  countNonGeometricClasses(
    modelId: string,
  ): Array<{ category: string; count: number; ids: number[] }> {
    const candidates = [
      'IFCSYSTEM',
      'IFCDISTRIBUTIONSYSTEM',
      'IFCBUILDINGSYSTEM',
      'IFCGROUP',
      'IFCZONE',
      'IFCSTRUCTURALANALYSISMODEL',
      'IFCELEMENTASSEMBLY',
      'IFCGRID',
      'IFCGRIDAXIS',
      'IFCSPATIALZONE',
      'IFCRELASSIGNSTOGROUP',
      'IFCRELSERVICESBUILDINGS',
    ]
    const out: Array<{ category: string; count: number; ids: number[] }> = []
    for (const name of candidates) {
      const ids = this.getLineIDsByType(modelId, name)
      if (ids.length > 0) {
        out.push({ category: name, count: ids.length, ids })
      }
    }
    return out
  }

  /** Read attributes (Name, Description) of the given express ID. */
  getAttributes(
    modelId: string,
    expressID: number,
  ): { name: string | null; description: string | null } | null {
    const entry = this.models.get(modelId)?.entry
    if (!entry) return null
    try {
      const e = entry.api.GetLine(entry.modelID, expressID, true, false) as {
        Name?: { value?: unknown }
        Description?: { value?: unknown }
      } | null
      if (!e) return null
      const name =
        e.Name && typeof e.Name === 'object' && 'value' in e.Name
          ? String(e.Name.value ?? '') || null
          : null
      const description =
        e.Description && typeof e.Description === 'object' && 'value' in e.Description
          ? String(e.Description.value ?? '') || null
          : null
      return { name, description }
    } catch {
      return null
    }
  }

  /** RelatedObjects expressIDs of an IfcGroup / IfcSystem / IfcZone.
   * Reads from a per-model cache that scans every
   * IfcRelAssignsToGroup once (much more reliable than relying on
   * the IsGroupedBy inverse, which web-ifc does not always populate
   * when GetLine is called with flatten=true + inverse=true). */
  getGroupMembers(modelId: string, groupExpressID: number): number[] {
    const map = this.ensureGroupMembersMap(modelId)
    return map?.get(groupExpressID) ?? []
  }

  /** Number of members of every group/system, keyed by expressID.
   * Lets callers display counts without paying the per-group lookup
   * cost. Returns an empty Map when the model isn't loaded. */
  getGroupMemberCounts(modelId: string): Map<number, number> {
    const map = this.ensureGroupMembersMap(modelId)
    if (!map) return new Map()
    const out = new Map<number, number>()
    for (const [id, ids] of map) out.set(id, ids.length)
    return out
  }

  /** Elements contained in a spatial structure node (IfcSpace,
   * IfcBuildingStorey, IfcSite, ...) via
   * IfcRelContainedInSpatialStructure. Distinct from group membership:
   * the IFC schema uses RelatingStructure → contained elements
   * here, not RelatingGroup → members. Returns expressIDs of the
   * RelatedElements. Cached per model, per spatial parent.
   * WORKAROUND-WEBIFC-0.0.77: same defensive parse as
   * ensureGroupMembersMap. */
  getContainedElements(
    modelId: string,
    spatialExpressID: number,
  ): number[] {
    const map = this.ensureContainmentMap(modelId)
    return map?.get(spatialExpressID) ?? []
  }

  /** Counts of contained elements per spatial parent. */
  getContainmentCounts(modelId: string): Map<number, number> {
    const map = this.ensureContainmentMap(modelId)
    if (!map) return new Map()
    const out = new Map<number, number>()
    for (const [id, ids] of map) out.set(id, ids.length)
    return out
  }

  /** IfcRelSpaceBoundary records that mention the given IfcSpace.
   *  Returns an empty list when the model isn't loaded or the space
   *  has no boundaries authored. Lazy: the scan happens on first
   *  call and is cached for the lifetime of the parser. */
  getBoundariesOfSpace(
    modelId: string,
    spaceExpressID: number,
  ): RawSpaceBoundary[] {
    const idx = this.ensureSpaceBoundaryIndex(modelId)
    return idx?.bySpace.get(spaceExpressID) ?? []
  }

  /** IfcRelSpaceBoundary records that mention the given building
   *  element (wall, slab, window, …). The inverse attribute is
   *  IfcElement.ProvidesBoundaries: Fragments doesn't surface it,
   *  so the panel relies on this. */
  getBoundariesOfElement(
    modelId: string,
    elementExpressID: number,
  ): RawSpaceBoundary[] {
    const idx = this.ensureSpaceBoundaryIndex(modelId)
    return idx?.byElement.get(elementExpressID) ?? []
  }

  /** All inverse-relation entries for the given express ID that
   *  Fragments doesn't reliably populate. Each entry carries the
   *  IFC inverse-attribute name (e.g. HasCoverings, IsNestedBy) and
   *  the express ID of the related entity. The PropertiesPanel
   *  groups these by name and folds them into the "Relações
   *  inversas" section, deduping against whatever Fragments already
   *  surfaced.
   *
   *  The scan is lazy and cached per model; first call enumerates
   *  every IfcRel* line of the supported subtypes and builds the
   *  bidirectional map. Cost is O(rels) once per model. */
  getExtraInverseRelations(
    modelId: string,
    expressID: number,
  ): Array<{ inverseName: string; targetExpressID: number }> {
    const idx = this.ensureExtraInverseIndex(modelId)
    return idx?.get(expressID) ?? []
  }

  // Spec table that drives the generic inverse-relation scanner.
  // Each row says: read this IFC rel subtype, point its relating
  // side at its related side(s), and surface entries under these
  // inverse-attribute names. Some Fragments-supplied inverses are
  // intentionally re-emitted (Decomposes/IsDecomposedBy/etc.) so a
  // model that comes from a partial exporter still gets them; the
  // panel dedupes against what Fragments already returned, so
  // duplicates are silent.
  private static readonly REL_SPECS: Array<{
    type: string
    relating: string
    related: string
    relatedIsList: boolean
    /** Inverse to surface on the relating-side entity. Null = skip. */
    onRelating: string | null
    /** Inverse to surface on each related-side entity. Null = skip. */
    onRelated: string | null
  }> = [
    // Spatial containment + reference (Fragments gives the contained
    // side's ContainedInStructure but never the container's
    // ContainsElements / ReferencesElements inverse).
    {
      type: 'IFCRELCONTAINEDINSPATIALSTRUCTURE',
      relating: 'RelatingStructure',
      related: 'RelatedElements',
      relatedIsList: true,
      onRelating: 'ContainsElements',
      onRelated: null,
    },
    {
      type: 'IFCRELREFERENCEDINSPATIALSTRUCTURE',
      relating: 'RelatingStructure',
      related: 'RelatedElements',
      relatedIsList: true,
      onRelating: 'ReferencesElements',
      onRelated: null,
    },
    // Aggregation / nesting / declaration.
    {
      type: 'IFCRELAGGREGATES',
      relating: 'RelatingObject',
      related: 'RelatedObjects',
      relatedIsList: true,
      onRelating: 'IsDecomposedBy',
      onRelated: 'Decomposes',
    },
    {
      type: 'IFCRELNESTS',
      relating: 'RelatingObject',
      related: 'RelatedObjects',
      relatedIsList: true,
      onRelating: 'IsNestedBy',
      onRelated: 'Nests',
    },
    {
      type: 'IFCRELDECLARES',
      relating: 'RelatingContext',
      related: 'RelatedDefinitions',
      relatedIsList: true,
      onRelating: 'Declares',
      onRelated: 'HasContext',
    },
    // Connectivity (walls connected to walls, generic element
    // connections, structural realizations).
    {
      type: 'IFCRELCONNECTSELEMENTS',
      relating: 'RelatingElement',
      related: 'RelatedElement',
      relatedIsList: false,
      onRelating: 'ConnectedTo',
      onRelated: 'ConnectedFrom',
    },
    {
      type: 'IFCRELCONNECTSWITHREALIZINGELEMENTS',
      relating: 'RelatingElement',
      related: 'RelatedElement',
      relatedIsList: false,
      onRelating: 'ConnectedTo',
      onRelated: 'ConnectedFrom',
    },
    {
      type: 'IFCRELCONNECTSPATHELEMENTS',
      relating: 'RelatingElement',
      related: 'RelatedElement',
      relatedIsList: false,
      onRelating: 'ConnectedTo',
      onRelated: 'ConnectedFrom',
    },
    // Coverings (finishes, paint, ceilings, floors).
    {
      type: 'IFCRELCOVERSBLDGELEMENTS',
      relating: 'RelatingBuildingElement',
      related: 'RelatedCoverings',
      relatedIsList: true,
      onRelating: 'HasCoverings',
      onRelated: 'CoversElements',
    },
    {
      type: 'IFCRELCOVERSSPACES',
      relating: 'RelatingSpace',
      related: 'RelatedCoverings',
      relatedIsList: true,
      onRelating: 'HasCoverings',
      onRelated: 'CoversSpaces',
    },
    // Voids / fillings / projections.
    {
      type: 'IFCRELVOIDSELEMENT',
      relating: 'RelatingBuildingElement',
      related: 'RelatedOpeningElement',
      relatedIsList: false,
      onRelating: 'HasOpenings',
      onRelated: 'VoidsElements',
    },
    {
      type: 'IFCRELFILLSELEMENT',
      relating: 'RelatingOpeningElement',
      related: 'RelatedBuildingElement',
      relatedIsList: false,
      onRelating: 'HasFillings',
      onRelated: 'FillsVoids',
    },
    {
      type: 'IFCRELPROJECTSELEMENT',
      relating: 'RelatingElement',
      related: 'RelatedFeatureElement',
      relatedIsList: false,
      onRelating: 'HasProjections',
      onRelated: 'ProjectsElements',
    },
    // Interferences (clash detection authoring, IFC 4.x).
    {
      type: 'IFCRELINTERFERESELEMENTS',
      relating: 'RelatingElement',
      related: 'RelatedElement',
      relatedIsList: false,
      onRelating: 'InterferesElements',
      onRelated: 'IsInterferedByElements',
    },
    // Process sequence (4D).
    {
      type: 'IFCRELSEQUENCE',
      relating: 'RelatingProcess',
      related: 'RelatedProcess',
      relatedIsList: false,
      onRelating: 'IsPredecessorTo',
      onRelated: 'IsSuccessorFrom',
    },
    // System ↔ buildings.
    {
      type: 'IFCRELSERVICESBUILDINGS',
      relating: 'RelatingSystem',
      related: 'RelatedBuildings',
      relatedIsList: true,
      onRelating: 'ServicesBuildings',
      onRelated: 'ServicedBySystems',
    },
  ]

  private ensureExtraInverseIndex(
    modelId: string,
  ): Map<number, Array<{ inverseName: string; targetExpressID: number }>> | null {
    const entry = this.models.get(modelId)?.entry
    if (!entry) return null
    const cached = (entry as ModelEntry & {
      extraInverseIndex?: Map<
        number,
        Array<{ inverseName: string; targetExpressID: number }>
      >
    }).extraInverseIndex
    if (cached) return cached
    const map = new Map<
      number,
      Array<{ inverseName: string; targetExpressID: number }>
    >()
    const push = (
      fromId: number,
      inverseName: string,
      targetId: number,
    ): void => {
      let bucket = map.get(fromId)
      if (!bucket) {
        bucket = []
        map.set(fromId, bucket)
      }
      bucket.push({ inverseName, targetExpressID: targetId })
    }
    let totalScanned = 0
    let totalParsed = 0
    let totalFailed = 0
    for (const spec of RawIfcStore.REL_SPECS) {
      const relIds = this.getLineIDsByType(modelId, spec.type)
      for (const relId of relIds) {
        totalScanned++
        let line: Record<string, unknown> | null
        try {
          line = entry.api.GetLine(
            entry.modelID,
            relId,
            false,
            false,
          ) as Record<string, unknown> | null
        } catch {
          totalFailed++
          continue
        }
        if (!line) continue
        const relatingId = extractExpressID(line[spec.relating])
        if (relatingId == null) continue
        const relatedRaw = line[spec.related]
        const relatedIds: number[] = []
        if (spec.relatedIsList) {
          if (!Array.isArray(relatedRaw)) continue
          for (const r of relatedRaw) {
            const id = extractExpressID(r)
            if (id != null) relatedIds.push(id)
          }
        } else {
          const id = extractExpressID(relatedRaw)
          if (id != null) relatedIds.push(id)
        }
        if (relatedIds.length === 0) continue
        if (spec.onRelating) {
          for (const tid of relatedIds) push(relatingId, spec.onRelating, tid)
        }
        if (spec.onRelated) {
          for (const tid of relatedIds) push(tid, spec.onRelated, relatingId)
        }
        totalParsed++
      }
    }
    if (totalScanned > 0) {
      console.info(
        '[rawIfc] generic inverse-relation scan: ' +
          totalParsed +
          '/' +
          totalScanned +
          ' rel(s) parsed (' +
          totalFailed +
          ' failed) → ' +
          map.size +
          ' entit(y/ies) with extra inverses',
      )
    }
    ;(entry as ModelEntry & {
      extraInverseIndex?: Map<
        number,
        Array<{ inverseName: string; targetExpressID: number }>
      >
    }).extraInverseIndex = map
    return map
  }

  /** Lookup (category, name, guid) of an arbitrary express ID via
   *  the web-ifc parser. Useful for the property panel when it needs
   *  to render a navigable target whose only known field is the
   *  numeric id (e.g. the other side of an IfcRelSpaceBoundary).
   *  Returns null when the model isn't loaded or the line is
   *  unreadable. */
  getEntitySummary(
    modelId: string,
    expressID: number,
  ): { category: string; name: string | null; guid: string | null } | null {
    const entry = this.models.get(modelId)?.entry
    if (!entry) return null
    let line: Record<string, unknown> | null
    try {
      line = entry.api.GetLine(
        entry.modelID,
        expressID,
        false,
        false,
      ) as Record<string, unknown> | null
    } catch {
      return null
    }
    if (!line) return null
    let category = ''
    if (typeof line.type === 'number') {
      let n = entry.typeNames.get(line.type)
      if (n === undefined) {
        try {
          n = entry.api.GetNameFromTypeCode(line.type) || ''
        } catch {
          n = ''
        }
        entry.typeNames.set(line.type, n)
      }
      category = n ?? ''
    }
    const nameAttr = line.Name
    let name: string | null = null
    if (nameAttr && typeof nameAttr === 'object' && 'value' in nameAttr) {
      const v = (nameAttr as { value?: unknown }).value
      name = typeof v === 'string' && v ? v : null
    } else if (typeof nameAttr === 'string' && nameAttr) {
      name = nameAttr
    }
    const guidAttr = line.GlobalId
    let guid: string | null = null
    if (guidAttr && typeof guidAttr === 'object' && 'value' in guidAttr) {
      const v = (guidAttr as { value?: unknown }).value
      guid = typeof v === 'string' && v ? v : null
    } else if (typeof guidAttr === 'string' && guidAttr) {
      guid = guidAttr
    }
    return { category, name, guid }
  }

  private ensureSpaceBoundaryIndex(modelId: string): {
    bySpace: Map<number, RawSpaceBoundary[]>
    byElement: Map<number, RawSpaceBoundary[]>
  } | null {
    const entry = this.models.get(modelId)?.entry
    if (!entry) return null
    const cached = (entry as ModelEntry & {
      spaceBoundaryIndex?: {
        bySpace: Map<number, RawSpaceBoundary[]>
        byElement: Map<number, RawSpaceBoundary[]>
      }
    }).spaceBoundaryIndex
    if (cached) return cached
    const bySpace = new Map<number, RawSpaceBoundary[]>()
    const byElement = new Map<number, RawSpaceBoundary[]>()
    // GetLineIDsWithType does NOT include subtypes; we scan the
    // base type AND both 1stLevel/2ndLevel subtypes explicitly.
    const subTypes = [
      'IFCRELSPACEBOUNDARY',
      'IFCRELSPACEBOUNDARY1STLEVEL',
      'IFCRELSPACEBOUNDARY2NDLEVEL',
    ]
    let scanned = 0
    let parsed = 0
    let failed = 0
    for (const subType of subTypes) {
      const relIds = this.getLineIDsByType(modelId, subType)
      for (const relId of relIds) {
        scanned++
        let line: Record<string, unknown> | null
        try {
          line = entry.api.GetLine(
            entry.modelID,
            relId,
            false,
            false,
          ) as Record<string, unknown> | null
        } catch {
          failed++
          continue
        }
        if (!line) continue
        const spaceId = extractExpressID(line.RelatingSpace)
        const elementId = extractExpressID(line.RelatedBuildingElement)
        if (spaceId == null) continue
        let relCategory = subType
        if (typeof line.type === 'number') {
          let n = entry.typeNames.get(line.type)
          if (n === undefined) {
            try {
              n = entry.api.GetNameFromTypeCode(line.type) || ''
            } catch {
              n = ''
            }
            entry.typeNames.set(line.type, n)
          }
          if (n) relCategory = n
        }
        const cgId = extractExpressID(line.ConnectionGeometry)
        let area: number | null = null
        if (cgId != null) {
          area = this.computeBoundaryAreaFromConnectionGeometry(entry, cgId)
        }
        const record: RawSpaceBoundary = {
          relExpressID: relId,
          relCategory,
          spaceExpressID: spaceId,
          elementExpressID: elementId,
          name: extractEnumOrString(line.Name),
          physicalOrVirtual: extractEnumOrString(line.PhysicalOrVirtualBoundary),
          internalOrExternal: extractEnumOrString(line.InternalOrExternalBoundary),
          connectionGeometryExpressID: cgId,
          area,
        }
        let bucketSpace = bySpace.get(spaceId)
        if (!bucketSpace) {
          bucketSpace = []
          bySpace.set(spaceId, bucketSpace)
        }
        bucketSpace.push(record)
        if (elementId != null) {
          let bucketElem = byElement.get(elementId)
          if (!bucketElem) {
            bucketElem = []
            byElement.set(elementId, bucketElem)
          }
          bucketElem.push(record)
        }
        parsed++
      }
    }
    if (scanned > 0) {
      console.info(
        '[rawIfc] IfcRelSpaceBoundary scan: ' +
          parsed +
          '/' +
          scanned +
          ' parsed (' +
          failed +
          ' failed) → ' +
          bySpace.size +
          ' space(s), ' +
          byElement.size +
          ' element(s) with boundaries',
      )
    }
    const idx = { bySpace, byElement }
    ;(entry as ModelEntry & {
      spaceBoundaryIndex?: {
        bySpace: Map<number, RawSpaceBoundary[]>
        byElement: Map<number, RawSpaceBoundary[]>
      }
    }).spaceBoundaryIndex = idx
    return idx
  }

  private ensureContainmentMap(
    modelId: string,
  ): Map<number, number[]> | null {
    const entry = this.models.get(modelId)?.entry
    if (!entry) return null
    const cached = (entry as ModelEntry & {
      containmentMap?: Map<number, number[]>
    }).containmentMap
    if (cached) return cached
    const map = new Map<number, number[]>()
    const relIds = this.getLineIDsByType(
      modelId,
      'IFCRELCONTAINEDINSPATIALSTRUCTURE',
    )
    for (const relId of relIds) {
      let line: Record<string, unknown> | null
      try {
        line = entry.api.GetLine(
          entry.modelID,
          relId,
          false,
          false,
        ) as Record<string, unknown> | null
      } catch {
        continue
      }
      if (!line) continue
      const parentId = extractExpressID(line.RelatingStructure)
      if (parentId == null) continue
      const elems = line.RelatedElements
      if (!Array.isArray(elems) || elems.length === 0) continue
      let bucket = map.get(parentId)
      if (!bucket) {
        bucket = []
        map.set(parentId, bucket)
      }
      for (const ref of elems) {
        const id = extractExpressID(ref)
        if (id != null) bucket.push(id)
      }
    }
    ;(entry as ModelEntry & { containmentMap?: Map<number, number[]> }).containmentMap = map
    return map
  }

  /** Inverse lookup: every IfcGroup / IfcSystem the given element is
   * assigned to (via IfcRelAssignsToGroup). Returns an empty list when
   * the element has no group memberships, the model isn't loaded, or
   * the lookup fails. Resolves Name/category from web-ifc on demand. */
  getGroupsOfElement(
    modelId: string,
    elementExpressID: number,
  ): Array<{ expressID: number; category: string; name: string | null }> {
    const map = this.ensureGroupMembersMap(modelId)
    if (!map) return []
    const entry = this.models.get(modelId)?.entry
    if (!entry) return []
    const out: Array<{
      expressID: number
      category: string
      name: string | null
    }> = []
    for (const [groupId, members] of map) {
      if (!members.includes(elementExpressID)) continue
      let category = ''
      let name: string | null = null
      try {
        const line = entry.api.GetLine(
          entry.modelID,
          groupId,
          false,
          false,
        ) as {
          type?: number
          Name?: { value?: unknown } | string | null
        } | null
        if (line) {
          if (typeof line.type === 'number') {
            let n = entry.typeNames.get(line.type)
            if (n === undefined) {
              try {
                n = entry.api.GetNameFromTypeCode(line.type) || ''
              } catch {
                n = ''
              }
              entry.typeNames.set(line.type, n)
            }
            category = n ?? ''
          }
          const nameAttr = line.Name
          if (nameAttr && typeof nameAttr === 'object' && 'value' in nameAttr) {
            const v = (nameAttr as { value?: unknown }).value
            name = typeof v === 'string' && v ? v : null
          } else if (typeof nameAttr === 'string' && nameAttr) {
            name = nameAttr
          }
        }
      } catch {
        /* ignore, we still surface the group, just without its name */
      }
      out.push({ expressID: groupId, category, name })
    }
    return out
  }

  private ensureGroupMembersMap(
    modelId: string,
  ): Map<number, number[]> | null {
    const entry = this.models.get(modelId)?.entry
    if (!entry) return null
    if (entry.groupMembers) return entry.groupMembers
    const map = new Map<number, number[]>()
    const diag: Record<string, { rels: number; mapped: number }> = {}

    // IfcRelAssignsToGroup is the canonical link System↔members. Some
    // exporters use IfcRelAggregates (RelatingObject = system) or
    // IfcRelNests instead, so we try all three. Web-ifc's
    // GetLineIDsWithType does not include subtypes; each must be
    // enumerated explicitly. The "relating" attribute name differs
    // per type, hence the per-rel pair.
    const relSpecs: Array<{ type: string; relating: string }> = [
      { type: 'IFCRELASSIGNSTOGROUP', relating: 'RelatingGroup' },
      { type: 'IFCRELASSIGNSTOGROUPBYFACTOR', relating: 'RelatingGroup' },
      { type: 'IFCRELAGGREGATES', relating: 'RelatingObject' },
      { type: 'IFCRELNESTS', relating: 'RelatingObject' },
    ]

    let failedLines = 0
    for (const spec of relSpecs) {
      const relIds = this.getLineIDsByType(modelId, spec.type)
      diag[spec.type] = { rels: relIds.length, mapped: 0 }
      for (const relId of relIds) {
        // Fallback path: web-ifc 0.0.77 throws "Cannot convert
        // '1,2,3' to unsigned int" on lines whose argument tape
        // contains LIST-OF-LIST tokens that confuse the typed
        // constructor (e.g. polygonal-face indices on neighbouring
        // entities). When the typed GetLine fails we fall back to
        // GetRawLineData and parse the raw STEP arguments by index.
        let line: Record<string, unknown> | null
        let raw: { type?: number; arguments?: unknown[] } | null = null
        try {
          line = entry.api.GetLine(
            entry.modelID,
            relId,
            false,
            false,
          ) as Record<string, unknown> | null
        } catch {
          line = null
        }
        if (!line) {
          try {
            raw = entry.api.GetRawLineData(entry.modelID, relId) as {
              type?: number
              arguments?: unknown[]
            } | null
          } catch {
            failedLines++
            continue
          }
        }
        // Same argument order across the four rel types we walk:
        // [GlobalId, OwnerHistory, Name, Description, RelatedObjects,
        //  RelatedObjectsType, RelatingGroup/RelatingObject].
        const relatingRaw = line
          ? line[spec.relating]
          : raw?.arguments?.[6]
        const objsRaw = line
          ? line.RelatedObjects
          : raw?.arguments?.[4]
        const groupId = extractExpressID(relatingRaw)
        if (groupId == null) continue
        if (!Array.isArray(objsRaw) || objsRaw.length === 0) continue
        let bucket = map.get(groupId)
        if (!bucket) {
          bucket = []
          map.set(groupId, bucket)
        }
        let added = 0
        for (const ref of objsRaw) {
          const id = extractExpressID(ref)
          if (id != null) {
            bucket.push(id)
            added++
          }
        }
        if (added > 0) diag[spec.type]!.mapped += 1
      }
    }
    const totalRels = Object.values(diag).reduce((acc, v) => acc + v.rels, 0)
    const totalMembers = Array.from(map.values()).reduce(
      (acc, v) => acc + v.length,
      0,
    )
    if (failedLines > 0) {
      console.warn(
        '[rawIfc] ' +
          failedLines +
          ' relation line(s) rejected by web-ifc and skipped (' +
          (totalRels - failedLines) +
          '/' +
          totalRels +
          ' parsed OK).',
      )
    } else {
      console.info(
        '[rawIfc] all ' +
          totalRels +
          ' relation lines parsed OK → ' +
          map.size +
          ' group(s), ' +
          totalMembers +
          ' member assignment(s) total.',
      )
    }
    // Surface the breakdown so the user can confirm in DevTools that
    // the IFC actually has the relations we expected. The Snowdon
    // Towers HVAC export, for instance, should show
    // IFCRELASSIGNSTOGROUP > 0; if it's all zeros the IFC author used
    // a non-standard pattern and we need to extend relSpecs.
    console.info(
      '[rawIfc] group/system membership scan',
      Object.fromEntries(
        Object.entries(diag).filter(([, v]) => v.rels > 0),
      ),
      `→ ${map.size} group(s) populated`,
    )
    entry.groupMembers = map
    return map
  }

  /** ExpressIDs of every line of the given IFC type for this model.
   * Empty list when the model isn't loaded or web-ifc doesn't know the
   * type. Used by the IFC diff engine to enumerate IfcRoot subtypes. */
  getLineIDsByType(modelId: string, ifcTypeName: string): number[] {
    const entry = this.models.get(modelId)?.entry
    if (!entry) return []
    let typeCode: number
    try {
      typeCode = entry.api.GetTypeCodeFromName(ifcTypeName)
    } catch {
      return []
    }
    if (!typeCode) return []
    let vec: { size: () => number; get: (i: number) => number }
    try {
      vec = entry.api.GetLineIDsWithType(entry.modelID, typeCode)
    } catch {
      return []
    }
    const n = vec.size()
    const out: number[] = []
    for (let i = 0; i < n; i++) out.push(vec.get(i) as number)
    return out
  }

  /** GlobalId → expressID for the non-geometric aggregation entities
   *  (IfcZone, IfcSystem, IfcGroup, IfcElementAssembly, ...). Fragments
   *  only maps GUIDs of items it indexes (geometry + spatial structure),
   *  so getLocalIdsByGuids returns null for these; BCF viewpoints that
   *  point at a zone/system by GlobalId rely on this fallback. Returns
   *  null when the model isn't loaded or the guid isn't one of these
   *  aggregation entities. Lazy + cached per model. */
  getLocalIdByGuid(modelId: string, guid: string): number | null {
    const idx = this.ensureAggregationGuidIndex(modelId)
    return idx?.get(guid) ?? null
  }

  private ensureAggregationGuidIndex(
    modelId: string,
  ): Map<string, number> | null {
    const entry = this.models.get(modelId)?.entry
    if (!entry) return null
    if (entry.aggregationGuidIndex) return entry.aggregationGuidIndex
    const map = new Map<string, number>()
    // Same family as PropertiesPanel's isGroupLike: the entities that
    // group other elements and carry no geometry of their own.
    const TYPES = [
      'IFCZONE',
      'IFCSYSTEM',
      'IFCDISTRIBUTIONSYSTEM',
      'IFCBUILDINGSYSTEM',
      'IFCGROUP',
      'IFCELEMENTASSEMBLY',
      'IFCSPATIALZONE',
      'IFCSTRUCTURALANALYSISMODEL',
    ]
    for (const t of TYPES) {
      for (const id of this.getLineIDsByType(modelId, t)) {
        const summary = this.getEntitySummary(modelId, id)
        if (summary?.guid) map.set(summary.guid, id)
      }
    }
    entry.aggregationGuidIndex = map
    return map
  }

  // Reads IfcProject.UnitsInContext, finds the SI length unit and
  // returns its name with the SI prefix applied. Returns null when
  // the model has no project, no unit assignment, or no length unit
  // declared.
  //
  // Detetor de unidade baseado no PARSER (preferir quando o modelo já
  // está carregado no rawIfc: cobre IfcConversionBasedUnit / imperial).
  // O equivalente TEXTUAL, para quando não há parser e só temos os
  // bytes, é `detectProjectMetresPerUnit` em stepText/georefWrite.
  getLengthUnit(modelId: string): {
    label: string
    factorToMetres: number
  } | null {
    const entry = this.models.get(modelId)?.entry
    if (!entry) return null
    try {
      // IFCPROJECT type code: web-ifc gives us the code via name.
      const projectTypeCode = entry.api.GetTypeCodeFromName('IFCPROJECT')
      const projectIDs = entry.api.GetLineIDsWithType(
        entry.modelID,
        projectTypeCode,
      )
      if (!projectIDs || projectIDs.size() === 0) return null
      const projectId = projectIDs.get(0) as number
      const project = entry.api.GetLine(
        entry.modelID,
        projectId,
        true,
        false,
      ) as { UnitsInContext?: { Units?: unknown[] } } | null
      const units = project?.UnitsInContext?.Units
      if (!Array.isArray(units)) return null
      for (const u of units) {
        if (!u || typeof u !== 'object') continue
        const uo = u as Record<string, unknown>
        const utype =
          typeof uo.UnitType === 'object' && uo.UnitType !== null
            ? (uo.UnitType as { value?: unknown }).value
            : uo.UnitType
        if (utype !== 'LENGTHUNIT') continue
        const name =
          typeof uo.Name === 'object' && uo.Name !== null
            ? (uo.Name as { value?: unknown }).value
            : uo.Name
        const prefix =
          typeof uo.Prefix === 'object' && uo.Prefix !== null
            ? (uo.Prefix as { value?: unknown }).value
            : uo.Prefix
        // Unidade por conversão (FOOT, INCH): o factor vem do
        // ConversionFactor, não do nome.
        const porConversao = unidadePorConversao(uo)
        if (porConversao) return porConversao
        return interpretLengthUnit(String(name ?? ''), String(prefix ?? ''))
      }
      return null
    } catch (err) {
      console.warn('[rawIfc] getLengthUnit failed', err)
      return null
    }
  }

  // Returns the entity at expressID with all attribute references
  // resolved (flatten=true). Numeric IFC type codes are also rewritten
  // to their canonical string names (IfcWall, IfcLocalPlacement, ...).
  getEntity(modelId: string, expressID: number): unknown | null {
    const entry = this.models.get(modelId)?.entry
    if (!entry) return null
    try {
      const e = entry.api.GetLine(entry.modelID, expressID, true, false)
      if (e && typeof e === 'object') {
        this.resolveTypes(entry, e)
      }
      return e
    } catch (err) {
      // Downgraded from warn to debug: web-ifc 0.0.77 throws
      // "Cannot convert '...' to unsigned int" on entities whose raw
      // STEP arguments embed LIST-OF-LIST tokens (typically polygonal
      // face indices). The error is harmless to the rest of the
      // model: the property panel just falls back to showing the
      // Fragments-side data without the raw IFC details.
      // WORKAROUND-WEBIFC-0.0.77: remove when web-ifc fixes the
      // typed constructor for nested-list arguments.
      console.debug('[rawIfc] GetLine failed', expressID, err)
      return null
    }
  }

  // Walks the IfcLocalPlacement.PlacementRelTo chain. With flatten=true
  // PlacementRelTo is embedded directly in the parent placement, so we
  // traverse object references, not numeric IDs.
  getPlacementChain(modelId: string, elementExpressID: number): unknown[] {
    const entry = this.models.get(modelId)?.entry
    if (!entry) return []
    let element: unknown
    try {
      element = entry.api.GetLine(entry.modelID, elementExpressID, true, false)
    } catch {
      return []
    }
    let placement: unknown = (element as { ObjectPlacement?: unknown })
      ?.ObjectPlacement
    if (!placement || typeof placement !== 'object') return []

    const chain: unknown[] = []
    const seen = new Set<number>()
    while (placement && typeof placement === 'object' && chain.length < 50) {
      const id = (placement as { expressID?: unknown }).expressID
      if (typeof id === 'number') {
        if (seen.has(id)) break
        seen.add(id)
      }
      chain.push(placement)
      const next = (placement as { PlacementRelTo?: unknown }).PlacementRelTo
      placement = next && typeof next === 'object' ? next : null
    }
    for (const p of chain) {
      if (p && typeof p === 'object') this.resolveTypes(entry, p)
    }
    return chain
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn)
    return () => {
      this.listeners.delete(fn)
    }
  }

  /** Compute the planar area of an IfcConnectionGeometry line. We
   *  handle the two surface shapes that ~99% of exporters emit for
   *  IfcRelSpaceBoundary2ndLevel: IfcConnectionSurfaceGeometry with
   *  either IfcFaceSurface (FaceBound + PolyLoop) or
   *  IfcCurveBoundedPlane (IfcPlane + Polyline). Everything else
   *  (composite curves, parametric surfaces, swept surfaces) returns
   *  null: the caller surfaces "n/d" to the user instead of a wrong
   *  number. Area is in the IFC's intrinsic length-unit squared (no
   *  unit conversion here). */
  private computeBoundaryAreaFromConnectionGeometry(
    entry: ModelEntry,
    connectionGeomExpressID: number,
  ): number | null {
    let line: Record<string, unknown> | null
    try {
      line = entry.api.GetLine(
        entry.modelID,
        connectionGeomExpressID,
        true,
        false,
      ) as Record<string, unknown> | null
    } catch {
      return null
    }
    if (!line) return null
    this.resolveTypes(entry, line)
    const typeName = String(line.type ?? '').toUpperCase()
    // IfcConnectionSurfaceGeometry / IfcConnectionFaceGeometry both
    // place the surface under one of these attributes.
    const surface =
      (line.SurfaceOnRelatingElement as Record<string, unknown> | undefined) ??
      (line.FaceSurfaceOnRelatingElement as
        | Record<string, unknown>
        | undefined) ??
      null
    if (!surface) {
      if (typeName === 'IFCCONNECTIONPOINTGEOMETRY') return 0
      return null
    }
    return computeSurfaceArea(surface)
  }

  // Walk the (possibly cyclic) entity tree returned by web-ifc and
  // rewrite numeric `type` fields into their canonical IFC names.
  private resolveTypes(
    entry: ModelEntry,
    root: object,
    seen: WeakSet<object> = new WeakSet(),
  ): void {
    const stack: object[] = [root]
    while (stack.length > 0) {
      const cur = stack.pop()!
      if (seen.has(cur)) continue
      seen.add(cur)
      if (Array.isArray(cur)) {
        for (const item of cur) {
          if (item && typeof item === 'object') stack.push(item as object)
        }
        continue
      }
      const o = cur as Record<string, unknown>
      if (typeof o.type === 'number') {
        const code = o.type
        let name = entry.typeNames.get(code)
        if (name === undefined) {
          try {
            name = entry.api.GetNameFromTypeCode(code) || ''
          } catch {
            name = ''
          }
          entry.typeNames.set(code, name)
        }
        if (name) o.type = name
      }
      for (const k in o) {
        const v = o[k]
        if (v && typeof v === 'object') stack.push(v as object)
      }
    }
  }

  private notify() {
    for (const fn of this.listeners) fn()
  }
}

// Pull an expressID out of a web-ifc handle. Unflattened references
// look like { type: 5, value: <id> }; some edge cases (older web-ifc,
// already-resolved entities) yield a bare number or an object with an
// `expressID` field. Returns null when none of these apply.
function extractExpressID(ref: unknown): number | null {
  if (ref == null) return null
  if (typeof ref === 'number') return Number.isFinite(ref) ? ref : null
  if (typeof ref !== 'object') return null
  const o = ref as Record<string, unknown>
  if (typeof o.value === 'number' && Number.isFinite(o.value)) return o.value
  if (typeof o.expressID === 'number' && Number.isFinite(o.expressID)) {
    return o.expressID
  }
  return null
}

// Unwrap a web-ifc attribute that is either a raw string, an enum
// wrapper ({ value: 'PHYSICAL' }), or absent. Returns null when the
// value is empty/missing.
function extractEnumOrString(v: unknown): string | null {
  if (v == null) return null
  if (typeof v === 'string') return v || null
  if (typeof v === 'object' && 'value' in (v as object)) {
    const inner = (v as { value?: unknown }).value
    if (typeof inner === 'string' && inner) return inner
  }
  return null
}

function interpretLengthUnit(
  name: string,
  prefix: string,
): { label: string; factorToMetres: number } {
  const n = name.toUpperCase()
  const p = prefix.toUpperCase()
  const baseUnitFactor = n === 'METRE' ? 1 : 1
  const baseLabel = n === 'METRE' ? 'm' : n.toLowerCase()
  const prefixMap: Record<string, { f: number; abbr: string }> = {
    EXA: { f: 1e18, abbr: 'E' },
    PETA: { f: 1e15, abbr: 'P' },
    TERA: { f: 1e12, abbr: 'T' },
    GIGA: { f: 1e9, abbr: 'G' },
    MEGA: { f: 1e6, abbr: 'M' },
    KILO: { f: 1e3, abbr: 'k' },
    HECTO: { f: 1e2, abbr: 'h' },
    DECA: { f: 10, abbr: 'da' },
    DECI: { f: 1e-1, abbr: 'd' },
    CENTI: { f: 1e-2, abbr: 'c' },
    MILLI: { f: 1e-3, abbr: 'm' },
    MICRO: { f: 1e-6, abbr: 'μ' },
    NANO: { f: 1e-9, abbr: 'n' },
  }
  const pf = prefixMap[p]
  if (pf) {
    return {
      label: `${pf.abbr}${baseLabel}`,
      factorToMetres: pf.f * baseUnitFactor,
    }
  }
  return { label: baseLabel, factorToMetres: baseUnitFactor }
}

/** Valor numérico de um atributo do web-ifc: número cru, { value }, ou o
 *  objecto de medida com _representationValue / _internalValue. */
function valorNumerico(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  for (const k of ['value', '_representationValue', '_internalValue']) {
    const n = Number(o[k])
    if (o[k] != null && Number.isFinite(n)) return n
  }
  return null
}

/** Uma IfcConversionBasedUnit de comprimento (achatada pelo GetLine):
 *  factor = ConversionFactor.ValueComponent x factor da UnitComponent
 *  (SI, ou outra por conversão). Null quando não é uma unidade por
 *  conversão que se saiba ler. */
function unidadePorConversao(
  uo: Record<string, unknown>,
  fundo = 0,
): { label: string; factorToMetres: number } | null {
  const cf = uo.ConversionFactor
  if (!cf || typeof cf !== 'object' || fundo > 4) return null
  const c = cf as Record<string, unknown>
  const valor = valorNumerico(c.ValueComponent)
  const base = c.UnitComponent
  if (valor === null || !base || typeof base !== 'object') return null
  const b = base as Record<string, unknown>
  const texto = (x: unknown) =>
    String((x && typeof x === 'object' ? (x as { value?: unknown }).value : x) ?? '')
  const daBase =
    unidadePorConversao(b, fundo + 1) ??
    (texto(b.Name).toUpperCase() === 'METRE' ? interpretLengthUnit('METRE', texto(b.Prefix)) : null)
  if (!daBase) return null
  return { label: texto(uo.Name).toLowerCase() || 'unidade', factorToMetres: valor * daBase.factorToMetres }
}

type Vec3 = [number, number, number]

// Walks an IfcSurface subtype and returns its area in length-unit².
// Handles IfcFaceSurface (with PolyLoop), IfcCurveBoundedPlane
// (with IfcPolyline outer + inner boundaries), and
// IfcFaceBasedSurfaceModel (sum of its faces). Falls through to
// null for shapes we don't tessellate (composite curves, swept
// surfaces, parametric trims).
function computeSurfaceArea(surface: Record<string, unknown>): number | null {
  const typeName = String(surface.type ?? '').toUpperCase()
  if (typeName === 'IFCFACESURFACE' || typeName === 'IFCFACE') {
    return faceArea(surface)
  }
  if (typeName === 'IFCCURVEBOUNDEDPLANE') {
    return curveBoundedPlaneArea(surface)
  }
  if (typeName === 'IFCFACEBASEDSURFACEMODEL') {
    const fbsm = surface.FbsmFaces as
      | Array<Record<string, unknown>>
      | undefined
    if (!Array.isArray(fbsm)) return null
    let total = 0
    let any = false
    for (const cfs of fbsm) {
      const faces = cfs.CfsFaces as
        | Array<Record<string, unknown>>
        | undefined
      if (!Array.isArray(faces)) continue
      for (const f of faces) {
        const a = faceArea(f)
        if (a != null) {
          total += a
          any = true
        }
      }
    }
    return any ? total : null
  }
  // IfcSurface of revolution / extrusion / Bspline / etc: would
  // need full geometry tessellation. Skip rather than return a
  // guessed number.
  return null
}

// IfcFace / IfcFaceSurface: Bounds is a SET OF IfcFaceBound;
// IfcFaceOuterBound contributes positively, the other bounds
// (holes) negatively. Each bound's `Bound` is an IfcLoop,
// usually IfcPolyLoop with `Polygon` of IfcCartesianPoint[].
function faceArea(face: Record<string, unknown>): number | null {
  const bounds = face.Bounds as
    | Array<Record<string, unknown>>
    | undefined
  if (!Array.isArray(bounds) || bounds.length === 0) return null
  let total = 0
  let outerFound = false
  for (const b of bounds) {
    const boundType = String(b.type ?? '').toUpperCase()
    const isOuter = boundType === 'IFCFACEOUTERBOUND'
    const orientationAttr = b.Orientation
    let orientation = true
    if (orientationAttr === false) orientation = false
    else if (
      orientationAttr &&
      typeof orientationAttr === 'object' &&
      'value' in (orientationAttr as object)
    ) {
      const v = (orientationAttr as { value?: unknown }).value
      if (v === false || v === 'F' || v === 0) orientation = false
    }
    const loop = b.Bound as Record<string, unknown> | undefined
    if (!loop) continue
    const pts = extractLoopPoints(loop)
    if (pts.length < 3) continue
    const a = polygonArea3D(pts)
    // Sign convention: outer bound adds, inner subtracts. The
    // Orientation flag flips the loop direction; for area we take
    // its magnitude regardless and let the bound-type rule the
    // sign. Bounds with unknown type default to outer (single-
    // boundary faces).
    if (isOuter || (!isOuter && bounds.length === 1)) {
      total += a
      outerFound = true
    } else {
      total -= a
    }
    // Suppress unused-var warning while keeping orientation read
    // for future use (some exporters reverse inner bounds in
    // place of using IfcFaceOuterBound).
    void orientation
  }
  if (!outerFound && total === 0) return null
  return Math.max(0, total)
}

// IfcCurveBoundedPlane: outer + optional inner IfcCurves on a
// basis IfcPlane. We tessellate Polyline outlines; composite
// curves and trimmed curves are skipped.
function curveBoundedPlaneArea(
  surface: Record<string, unknown>,
): number | null {
  const outer = surface.OuterBoundary as
    | Record<string, unknown>
    | undefined
  if (!outer) return null
  const outerPts = extractCurvePoints(outer)
  if (outerPts.length < 3) return null
  let total = polygonArea3D(outerPts)
  const inner = surface.InnerBoundaries as
    | Array<Record<string, unknown>>
    | undefined
  if (Array.isArray(inner)) {
    for (const c of inner) {
      const pts = extractCurvePoints(c)
      if (pts.length >= 3) total -= polygonArea3D(pts)
    }
  }
  return Math.max(0, total)
}

// IfcPolyLoop → Polygon: IfcCartesianPoint[]. Other IfcLoop
// subtypes (EdgeLoop, VertexLoop) are uncommon for boundary
// geometry; if we hit them we return [] and the surface area
// becomes null.
function extractLoopPoints(loop: Record<string, unknown>): Vec3[] {
  const type = String(loop.type ?? '').toUpperCase()
  if (type === 'IFCPOLYLOOP') {
    const polygon = loop.Polygon as
      | Array<Record<string, unknown>>
      | undefined
    if (!Array.isArray(polygon)) return []
    const out: Vec3[] = []
    for (const p of polygon) {
      const v = readCartesianPoint(p)
      if (v) out.push(v)
    }
    return out
  }
  return []
}

function extractCurvePoints(curve: Record<string, unknown>): Vec3[] {
  const type = String(curve.type ?? '').toUpperCase()
  if (type === 'IFCPOLYLINE') {
    const pts = curve.Points as
      | Array<Record<string, unknown>>
      | undefined
    if (!Array.isArray(pts)) return []
    const out: Vec3[] = []
    for (const p of pts) {
      const v = readCartesianPoint(p)
      if (v) out.push(v)
    }
    return out
  }
  return []
}

function readCartesianPoint(p: unknown): Vec3 | null {
  if (!p || typeof p !== 'object') return null
  const coords = (p as { Coordinates?: unknown }).Coordinates
  if (!Array.isArray(coords)) return null
  const x = unwrapNumber(coords[0])
  const y = unwrapNumber(coords[1])
  const z = unwrapNumber(coords[2]) ?? 0
  if (x == null || y == null) return null
  return [x, y, z]
}

function unwrapNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (v && typeof v === 'object' && 'value' in (v as object)) {
    const inner = (v as { value?: unknown }).value
    if (typeof inner === 'number' && Number.isFinite(inner)) return inner
  }
  return null
}

// Newell's method: robust area for any 3D polygon (convex or
// concave, in any plane). Returns the magnitude; sign would
// require an oriented normal, which we don't need.
function polygonArea3D(pts: Vec3[]): number {
  const n = pts.length
  if (n < 3) return 0
  let nx = 0
  let ny = 0
  let nz = 0
  for (let i = 0; i < n; i++) {
    const a = pts[i]
    const b = pts[(i + 1) % n]
    nx += (a[1] - b[1]) * (a[2] + b[2])
    ny += (a[2] - b[2]) * (a[0] + b[0])
    nz += (a[0] - b[0]) * (a[1] + b[1])
  }
  return Math.sqrt(nx * nx + ny * ny + nz * nz) / 2
}

export const rawIfc = new RawIfcStore()
